import { AfterViewInit, Component, input, OnInit, output, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { firstValueFrom, Subscription } from 'rxjs';
import { ComponentPortal } from '@angular/cdk/portal';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSlideToggle } from '@angular/material/slide-toggle';

import { ImageCropComponent } from '../../../base/image/image-crop/image-crop.component';

import { ImageFormat } from '../../../../state-management/model/enums/screen-size.enums';
import { FileType } from '../../../../state-management/model/enums/state.enums';
import { SpecifiedLogoCarouselImage } from '../base-logo-carousel/base-logo-carousel.component';

import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { ImageUploadService } from '../../../../services/image-upload.service';

import { v4 as uuidv4 } from 'uuid';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { DEFAULT_ROOT_FOLDER, MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD, MSG_CONFIRM_DELETE_IMAGE, MIN_LOGOS_IN_CAROUSEL, DEFAULT_LOGO_CAROUSEL_FILENAME, DEFAULT_LOGO_CAROUSEL_FOLDER } from '../../../../state-management/constants/global.constants';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialog, DialogData } from '../../../meta/website-config/website-config-form/website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { environment } from '../../../../../environments/environment';
import { LoggerService } from '../../../../services/logger.service';


@Component({
  selector: 'app-edit-logo-carousel',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSlideToggle,
    MatIcon,
    MatPaginator,
    MatSort,
    MatSortModule,
    MatButtonModule,
  ],
  templateUrl: './edit-logo-carousel.component.html',
  styleUrl: './edit-logo-carousel.component.scss',
})
export class EditLogoCarouselComponent implements OnInit, AfterViewInit {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly elemId = input.required<string>();
  readonly onClose = output<void>();

  private overlayRef: OverlayRef | null = null;
  private overlaySubscription: Subscription = new Subscription();
  private hasChangedConfig: boolean = false;

  public displayedColumns = ['logo', 'showInCarousel', 'actions'];
  public logoCarouselFolder = this.stateService.getLogoCarouselStoragePath();
  public logoCarouselFileName = DEFAULT_LOGO_CAROUSEL_FILENAME;
  public supportedFileTypes = [FileType.WEBP, FileType.JPEG];

  public logos = new MatTableDataSource<SpecifiedLogoCarouselImage>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit() {
    console.log('EditLogoCarouselComponent ngAfterViewInit - paginator and sort setup');
    this.logos.paginator = this.paginator;
    this.logos.sort = this.sort;

    this.logos.sortingDataAccessor = (item, property) => {
      const value = item[property as keyof SpecifiedLogoCarouselImage];

      if (typeof value === 'boolean' || typeof value === 'number') {
        if (item.isPlaceholder) {
          if (this.sort.direction === 'desc') {
            return Number.POSITIVE_INFINITY;
          } else if (this.sort.direction === 'asc') {
            return Number.NEGATIVE_INFINITY;
          } else {
            return 0;
          }
        } else {
          return value ? 1 : 0; // Treat boolean as 1 or 0 for sorting
        }
      }

      if (Array.isArray(value)) {
        if (item.isPlaceholder) {
          if (this.sort.direction === 'desc') return 'ZZZZZ'
          if (this.sort.direction === 'asc') return 'AAAAA'
          return '';
        }
        return value.join(', '); // Join array values for sorting
      }
      else return ''; // Default case for other types
    }
  }

  constructor(
    private logger: LoggerService,
    private overlay: Overlay,
    private dialog: MatDialog,
    private stateService: WebsiteStateService,
    private imageUploadService: ImageUploadService
  ) { }

  ngOnInit(): void {
    console.log('EditLogoCarouselComponent ngOnInit - initializing logos');
    this.initLogos();
  }


  public updateShowInCarousel() {
    this.hasChangedConfig = true;
  }

  public uploadLogo(logo: SpecifiedLogoCarouselImage) {
    this.openPortal(logo.uuid);
  }

  public async deleteLogo(logo: SpecifiedLogoCarouselImage) {
    if (this.logos.data.filter((logo) => !logo.isPlaceholder).length <= MIN_LOGOS_IN_CAROUSEL) {
      this.openGenericDialog(MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD);
      return;
    }

    const confirmDelete = await this.openGenericDialog(MSG_CONFIRM_DELETE_IMAGE);
    if (confirmDelete) {
      this.logos.data = this.logos.data.filter((l) => l.uuid !== logo.uuid);

      this.stateService.updateElement(this.elemId(), {
        config: {
          images: this.logos.data.filter((logo) => logo.showInCarousel).map((logo) => logo.uuid),
        }
      });
      this.hasChangedConfig = false;

      this.stateService.setAndSaveLogoCarousel(this.logos.data).then(() => {
        const storageFolder = `/${environment.shopId}/${DEFAULT_ROOT_FOLDER}/${DEFAULT_LOGO_CAROUSEL_FOLDER}/${logo.uuid}`;
        this.imageUploadService.dropStorageFolder(storageFolder).then(() => {
          this.logger.log('Logo deleted from storage');
        }).catch((err) => {
          this.logger.error('Error deleting logo from storage:', err);
        });
      }).catch((err) => {
        this.logger.error('Error updating logo carousel:', err);
      });
    } else {
      this.logger.log(`Löschen des Logos abgebrochen`);
    }
  }

  public confirm() {
    if (this.hasChangedConfig) {
      this.stateService.updateElement(this.elemId(), {
        config: {
          images: this.logos.data.filter((logo) => logo.showInCarousel).map((logo) => logo.uuid)
        }
      });
      this.hasChangedConfig = false;
    }
    this.onClose.emit();
  }

  public cancel() {
    this.onClose.emit();
  }

  public hasChanges() {
    return this.hasChangedConfig;
  }


  private initLogos() {
    const blankLogo: SpecifiedLogoCarouselImage[] = [{ uuid: uuidv4(), timestamp: Date.now(), showInCarousel: false, isPlaceholder: true }]
    const logoCarousel = JSON.parse(JSON.stringify(this.stateService.getLogoCarouselById(this.elemId())()));
    this.logos.data = blankLogo.concat(logoCarousel);
  }


  // ### Overlay methods for dialog portal ###
  private openPortal(uuid: string) {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      backdropClass: 'cdk-overlay-dark-backdrop', // Add a slightly darker backdrop
    });

    const portal = new ComponentPortal(ImageCropComponent);
    const componentRef = this.overlayRef.attach(portal);

    componentRef.instance.elemId = 'LogoCarousel/' + uuid;
    componentRef.instance.requestedFormats = [ImageFormat.Free];
    componentRef.instance.fileTypes = this.supportedFileTypes;
    componentRef.instance.mode = 'logo-carousel';
    componentRef.instance.maxWidth = 600;
    componentRef.instance.maxHeight = 600;

    this.overlaySubscription.add(
      componentRef.instance.imageSaved.subscribe(() => {
        this.logos.data = this.logos.data.map((logo) => {
          if (logo.uuid === uuid) {
            logo.isPlaceholder = false;
          }
          return logo;
        });

        this.stateService.setAndSaveLogoCarousel(this.logos.data).then(() => {
          this.initLogos();
        }).catch((err) => {
          this.logger.error('Error updating logo carousel:', err);
        });
      })
    );

    this.overlaySubscription.add(
      componentRef.instance.closePortal.subscribe(() => {
        this.closeOverlay();
      })
    );

    this.overlaySubscription.add(
      this.overlayRef.backdropClick().subscribe(() => {
        this.closeOverlay();
      })
    );
  }

  private closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private openGenericDialog(data: DialogData): Promise<boolean> {
    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }
}